type ObjectType = Record<PropertyKey, unknown>;

type PickByValue<OBJ_T, VALUE_T> = // From https://stackoverflow.com/a/55153000
    Pick<
        OBJ_T,
        {
            [K in keyof OBJ_T]: OBJ_T[K] extends VALUE_T ? K : never;
        }[keyof OBJ_T]
    >;

type ObjectEntries<OBJ_T> = // From https://stackoverflow.com/a/60142095
    {
        [K in keyof OBJ_T]: [keyof PickByValue<OBJ_T, OBJ_T[K]>, OBJ_T[K]];
    }[keyof OBJ_T][];

export const objectKeys = <O extends Object>(obj: O) =>
    Object.keys(obj) as Array<keyof O>;

export function objectEntries<OBJ_T extends ObjectType>(
    obj: OBJ_T,
): ObjectEntries<OBJ_T> {
    return Object.entries(obj) as ObjectEntries<OBJ_T>;
}

type EntriesType =
    | [PropertyKey, unknown][]
    | ReadonlyArray<readonly [PropertyKey, unknown]>;

// Existing Utils
type DeepWritable<OBJ_T> = {
    -readonly [P in keyof OBJ_T]: DeepWritable<OBJ_T[P]>;
};
type UnionToIntersection<UNION_T> = // From https://stackoverflow.com/a/50375286
    (UNION_T extends any ? (k: UNION_T) => void : never) extends (
        k: infer I,
    ) => void
        ? I
        : never;

// New Utils
type UnionObjectFromArrayOfPairs<ARR_T extends EntriesType> =
    DeepWritable<ARR_T> extends (infer R)[]
        ? R extends [infer key, infer val]
            ? { [prop in key & PropertyKey]: val }
            : never
        : never;
type MergeIntersectingObjects<ObjT> = { [key in keyof ObjT]: ObjT[key] };
type EntriesToObject<ARR_T extends EntriesType> = MergeIntersectingObjects<
    UnionToIntersection<UnionObjectFromArrayOfPairs<ARR_T>>
>;

// Modified version of https://github.com/feross/fromentries/blob/master/index.js
export function objectFromEntries<ARR_T extends EntriesType>(
    arr: ARR_T,
): EntriesToObject<ARR_T> {
    return [...arr].reduce((obj, [key, val]) => {
        obj[key] = val;
        return obj;
    }, {}) as EntriesToObject<ARR_T>;
}
